import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Input from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import Label from '@/components/basicComponents/label';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { AddButton } from '@/components/dealPipeline-page/modify/addButton';
import { defaultListData } from '@/components/dealPipeline-page/modify/constants';
import { DynamicSelectWrapper } from '@/components/dealPipeline-page/modify/radioWithDynamicSelects';
import { IListData } from '@/components/dealPipeline-page/modify/types';
import { addStrategies, delStrategies, onChangeStrategies } from '@/components/dealPipeline-page/modify/utils';
import { GET_CURRENT_SUB_ASSET_CLASSES_QUERY } from '@/components/dueDiligence-page/queries';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { RadioButtonWithNotification } from '../components';
import {
  editInvestorAllocationSettingsAlwaysShowBannerDescription,
  editInvestorAllocationSettingsAlwaysShowOptions,
  editInvestorAllocationSettingsBiteSizeBannerDescription,
  editInvestorAllocationSettingsBiteSizeOptions,
  editInvestorAllocationSettingsOnHoldOptions
} from '../constants';
import { UPDATE_LEGAL_ENTITY_MUTATION } from '../queries';
import {
  EditInvestorFormDevider,
  EditInvestorFormFieldsWrapper,
  EditInvestorFormFooter,
  EditInvestorModalPageForm,
  EditInvestorModalPageWrapper
} from '../styles';
import {
  EditInvestorAllocationSettingsAlwaysShowOptionEnum,
  EditInvestorAllocationSettingsBiteSizeOptionEnum,
  EditInvestorAllocationSettingsOnHoldOptionEnum,
  IEditInvestorFormData
} from '../types';

const sriSelectOptions = [
  { label: 'Not Specified', value: 'Not Specified' },
  { label: 'Priority', value: 'Priority' },
  { label: 'Interested', value: 'Interested' },
  { label: 'No', value: 'No' }
];

interface EditAllocationSettingsModalPageProps {
  allocationSettingsFormData: IEditInvestorFormData['allocationSettings'];
  refetchEntityOrGroupData: () => Promise<void>;
  handleCloseModalPage: () => void;
}

export const EditAllocationSettingsModalPage = ({
  allocationSettingsFormData,
  refetchEntityOrGroupData,
  handleCloseModalPage
}: EditAllocationSettingsModalPageProps) => {
  const params = useParams();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    control,
    formState: { isSubmitting },
    reset,
    watch,
    handleSubmit
  } = useForm<IEditInvestorFormData['allocationSettings']>();

  const entityId = params?.entityId || '';

  const [listSectorsToAvoid, setListSectorsToAvoid] = useState<IListData>(defaultListData);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateLegalEntity] = useMutation(UPDATE_LEGAL_ENTITY_MUTATION);

  useEffect(() => {
    if (!allocationSettingsFormData) return;
    reset(allocationSettingsFormData);
    setListSectorsToAvoid({ ...listSectorsToAvoid, chosen: allocationSettingsFormData.sectorsToAvoid || [] });
  }, [allocationSettingsFormData]);

  const { loading: listSectorsToAvoidLoading } = useQuery(GET_CURRENT_SUB_ASSET_CLASSES_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const sortedListSectorsToAvoid = [...data.getCurrentSubAssetClasses.values]
        .filter((sectorToAvoid: string) => sectorToAvoid !== '' && !allocationSettingsFormData?.sectorsToAvoid?.includes(sectorToAvoid))
        .sort((a, b) => a.localeCompare(b));
      setListSectorsToAvoid({ ...listSectorsToAvoid, list: sortedListSectorsToAvoid });
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    setListSectorsToAvoid((prevListSectorsToAvoid) => {
      return {
        ...prevListSectorsToAvoid,
        list: prevListSectorsToAvoid.list
          .filter((sectorToAvoid) => !listSectorsToAvoid.chosen.includes(sectorToAvoid))
          .sort((a, b) => a.localeCompare(b))
      };
    });
  }, [listSectorsToAvoid.chosen]);

  const onSubmit = async (data: IEditInvestorFormData['allocationSettings']) => {
    try {
      setErrorMessage(null);

      const { onHold, alwaysShow, custodied, unmanaged, sri, biteSizeOption, defaultBiteSize1, defaultBiteSize2 } = data;

      const ignoreBiteSize = biteSizeOption === EditInvestorAllocationSettingsBiteSizeOptionEnum.UseMinimumRequiredCommitment;
      const sectorsToAvoid = listSectorsToAvoid.chosen.filter(Boolean);

      await updateLegalEntity({
        variables: {
          data: {
            id: entityId,
            onHold,
            custodied,
            unmanaged,
            ...(sri && { impact: sri }),
            alwaysShow: alwaysShow === EditInvestorAllocationSettingsAlwaysShowOptionEnum.ShowAll,
            ignoreBiteSize,
            sectorsToAvoid,
            ...(!ignoreBiteSize && {
              defaultBiteSize1: parseFloat(defaultBiteSize1),
              defaultBiteSize2: parseFloat(defaultBiteSize2)
            })
          }
        }
      });

      await refetchEntityOrGroupData();
      handleCloseModalPage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  if (listSectorsToAvoidLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle="Edit Investor" />} />
        <PageTitle title="Allocation Settings" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <EditInvestorModalPageForm onSubmit={handleSubmit(onSubmit)}>
            <EditInvestorFormFieldsWrapper>
              <div>
                <Label size="md" tooltip="Flag this entity for no new commitments.">
                  On Hold
                </Label>
                <Controller
                  name="onHold"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <OnHoldRadioButtonWrapper isMobile={isMobile}>
                      {editInvestorAllocationSettingsOnHoldOptions.map((option, index) => (
                        <RadioButton
                          key={option + index}
                          name="onHold"
                          radioButtonText={option}
                          selectedOption={
                            value ? EditInvestorAllocationSettingsOnHoldOptionEnum.Yes : EditInvestorAllocationSettingsOnHoldOptionEnum.No
                          }
                          handleRadioChange={(event) => onChange(event.target.value === EditInvestorAllocationSettingsOnHoldOptionEnum.Yes)}
                        />
                      ))}
                    </OnHoldRadioButtonWrapper>
                  )}
                />
              </div>
              <div>
                <Label
                  size="md"
                  tooltip="Some entity types (e.g., IRA, DAF) require that a private fund be approved by the custodian in order to hold the asset. Flag this requirement here."
                  tooltipWidth={400}
                >
                  Custodian Approval Required
                </Label>
                <Controller
                  name="custodied"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <OnHoldRadioButtonWrapper isMobile={isMobile}>
                      {editInvestorAllocationSettingsOnHoldOptions.map((option, index) => (
                        <RadioButton
                          key={option + index}
                          name="custodied"
                          radioButtonText={option}
                          selectedOption={
                            value ? EditInvestorAllocationSettingsOnHoldOptionEnum.Yes : EditInvestorAllocationSettingsOnHoldOptionEnum.No
                          }
                          handleRadioChange={(event) => onChange(event.target.value === EditInvestorAllocationSettingsOnHoldOptionEnum.Yes)}
                        />
                      ))}
                    </OnHoldRadioButtonWrapper>
                  )}
                />
              </div>
              <RadioButtonWithNotification
                control={control}
                name="alwaysShow"
                label="Investor Review"
                options={editInvestorAllocationSettingsAlwaysShowOptions}
                bannerDescription={editInvestorAllocationSettingsAlwaysShowBannerDescription}
              />
              <RadioButtonWithNotification
                control={control}
                name="biteSizeOption"
                label="Set the default  amount for initial commitments:"
                options={editInvestorAllocationSettingsBiteSizeOptions}
                bannerDescription={editInvestorAllocationSettingsBiteSizeBannerDescription}
              />
              <AnimatePresence>
                {watch('biteSizeOption') === EditInvestorAllocationSettingsBiteSizeOptionEnum.SetSpecificDefaultBiteSize && (
                  <motion.div key="biteSize" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <TwoColumnWrapper isMobile={isMobile}>
                      <Controller
                        name="defaultBiteSize1"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: 'Fund is required'
                          }
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Input
                            label="Fund"
                            value={value ? formatMaskedDollarInputValue(value) : ''}
                            onChange={(e) => {
                              const formattedValue = formatDollarInputValue(e.target.value);
                              onChange(formattedValue);
                            }}
                            errorText={error?.message}
                            withDollarSign
                            isRequiredField
                            size="md"
                            styleType="form"
                          />
                        )}
                      />
                      <Controller
                        name="defaultBiteSize2"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: 'Direct is required'
                          }
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Input
                            label="Direct"
                            value={value ? formatMaskedDollarInputValue(value) : ''}
                            onChange={(e) => {
                              const formattedValue = formatDollarInputValue(e.target.value);
                              onChange(formattedValue);
                            }}
                            errorText={error?.message}
                            withDollarSign
                            isRequiredField
                            size="md"
                            styleType="form"
                          />
                        )}
                      />
                    </TwoColumnWrapper>
                  </motion.div>
                )}
              </AnimatePresence>
              <TwoColumnWrapper isMobile={isMobile}>
                <div>
                  <Label tooltip="Add any Sectors that should be flagged as unsuitable for this entity." tooltipWidth={400} size="md">
                    Sectors to avoid
                  </Label>
                  {listSectorsToAvoid.chosen.map((el, index) => (
                    <DynamicSelectWrapper
                      key={`sectors-${index}`}
                      loading={index === listSectorsToAvoid.chosen.length - 1 && listSectorsToAvoid.duplicate ? 1 : 0}
                    >
                      <Select
                        value={listSectorsToAvoid.chosen[index]}
                        previewValue={listSectorsToAvoid.chosen[index]}
                        setValue={(value) => onChangeStrategies(value, index, listSectorsToAvoid, setListSectorsToAvoid)}
                        options={listSectorsToAvoid.list.map((sectorToAvoid) => createSimpleSelectOption(sectorToAvoid))}
                        styleType={
                          listSectorsToAvoid.chosen[listSectorsToAvoid.chosen.length - 1] !== el ||
                          listSectorsToAvoid.chosen[listSectorsToAvoid.chosen.length - 1] === listSectorsToAvoid.duplicate
                            ? 'disabled'
                            : 'form'
                        }
                        size="md"
                        withSearch
                      />
                      <DelItemWrapper onClick={() => delStrategies(el, index, listSectorsToAvoid, setListSectorsToAvoid)}>
                        <CloseInfoIcon width={21} height={20} fill={themeVar('font.action')} />
                      </DelItemWrapper>
                    </DynamicSelectWrapper>
                  ))}
                  {!!listSectorsToAvoid.list.length && listSectorsToAvoid.chosen[listSectorsToAvoid.chosen.length - 1] !== '' && (
                    <AddButton text="Add" onClick={() => addStrategies(listSectorsToAvoid, setListSectorsToAvoid)} />
                  )}
                </div>
              </TwoColumnWrapper>
              <div>
                <Label
                  size="md"
                  tooltip="Flag this entity if you do not manage the portfolio but track it here for informational purposes."
                  tooltipWidth={400}
                >
                  Unmanaged
                </Label>
                <Controller
                  name="unmanaged"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <OnHoldRadioButtonWrapper isMobile={isMobile}>
                      {editInvestorAllocationSettingsOnHoldOptions.map((option, index) => (
                        <RadioButton
                          key={option + index}
                          name="unmanaged"
                          radioButtonText={option}
                          selectedOption={
                            value ? EditInvestorAllocationSettingsOnHoldOptionEnum.Yes : EditInvestorAllocationSettingsOnHoldOptionEnum.No
                          }
                          handleRadioChange={(event) => onChange(event.target.value === EditInvestorAllocationSettingsOnHoldOptionEnum.Yes)}
                        />
                      ))}
                    </OnHoldRadioButtonWrapper>
                  )}
                />
              </div>
              <TwoColumnWrapper isMobile={isMobile}>
                <Controller
                  name="sri"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value || ''}
                      setValue={onChange}
                      options={sriSelectOptions}
                      tooltipWidth={400}
                      tooltip="Indicate the level of importance this investor places on SRI."
                      errorText={error?.message}
                      label="SRI"
                      size="md"
                    />
                  )}
                />
              </TwoColumnWrapper>
              <EditInvestorFormDevider />
            </EditInvestorFormFieldsWrapper>
            <EditInvestorFormFooter>
              <Button styleType="outline" onClick={handleCloseModalPage} type="button" size="lg">
                Cancel
              </Button>
              <Button styleType={isSubmitting ? 'disabled' : 'default'} type="submit" size="lg">
                Save
              </Button>
            </EditInvestorFormFooter>
          </EditInvestorModalPageForm>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const OnHoldRadioButtonWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '0')};
`;

const DelItemWrapper = styled.div`
  position: relative;
  top: 3px;
  padding-left: 15px;
  cursor: pointer;
`;
